/**
 * LIRE UN NOMBRE FRACTIONNAIRE — « 8 1/12 » est huit et un douzième (tranche
 * T15, décision du fondateur du 11 octobre 2026 : « 8 1/12 ne doit pas être lu
 * 81/12, c'est une faute grave qui induit en erreur »).
 *
 * Les lecteurs d'une saisie retiraient toute espace : « 8 1/12 » devenait
 * « 81/12 » (6,75 au lieu de 8,083…), et `8\frac{1}{12}` — ce que le champ de
 * saisie de l'application écrit — était lu comme le produit 8 × 1/12. Une
 * bonne réponse était refusée, et une réponse fausse pouvait être dite juste.
 *
 * CE MODULE NE FAIT QU'UNE CHOSE, AVANT que les espaces ne soient retirées : il
 * repère « un entier, une séparation, une fraction » et le récrit sous une
 * forme que plus rien ne peut confondre. Tous les lecteurs l'appellent en
 * premier (`normaliser`, `latexVersCas`, `lireRationnel`).
 *
 *   « 8 1/12 », `8\frac{1}{12}`, `8\,\dfrac{1}{12}`   →  `\mixte{8}{1}{12}`
 *
 * LA RÈGLE, et ce qu'elle ne devine pas :
 *
 *   - la fraction est PLUS PETITE QUE 1 (numérateur de 1 à dénominateur − 1) :
 *     c'est un nombre fractionnaire, entier + fraction ;
 *   - elle ne l'est pas — « 3 4/2 », « 5 0/3 », un dénominateur nul : on NE
 *     DEVINE PAS. La séparation est remplacée par `SEPARATION_NON_LUE`, un signe
 *     qu'aucun lecteur ne sait lire : la saisie n'est alors ni 34/2 ni 3 + 4/2,
 *     le calcul n'a pas d'avis, et la main passe au modèle ;
 *   - une tranche de TROIS chiffres devant la barre, après un entier d'un à
 *     trois chiffres (« 12 500/2 »), est une tranche de MILLIERS : rien ne
 *     change, comme pour « 1 000 » et « 12 500,5 » ;
 *   - l'entier n'est pas la fin d'autre chose : ni un exposant (`x^2 1/3`), ni
 *     une décimale (« 2,5 1/2 »), ni un dénominateur (« 1/2 1/3 »), ni collé à
 *     une lettre (« 2x 1/3 »). Ce sont des produits, ou des écritures que l'on
 *     laisse aux autres lecteurs ;
 *   - le signe reste DEVANT : « −2 1/3 » devient `-\mixte{2}{1}{3}`, soit
 *     −(2 + 1/3).
 */
/** Ce qui sépare l'entier de sa fraction : espaces (insécables comprises), espaces LaTeX. */
const SEPARATION = String.raw `(?:\s|\\[,;:! ]|~)`;
/** L'entier n'est la suite de rien : ni chiffre, ni virgule, ni barre, ni exposant, ni lettre. */
const DEBUT = String.raw `(?<![\d.,/^_{}\\a-zA-Z])`;
const EN_LIGNE = new RegExp(String.raw `${DEBUT}(\d+)(${SEPARATION}+)(\d+)\s*/\s*(\d+)(?![\d.,/^])`, 'g');
const EN_FRACTION = new RegExp(String.raw `${DEBUT}(\d+)(${SEPARATION}*)\\[dt]?frac\s*\{\s*(\d+)\s*\}\s*\{\s*(\d+)\s*\}`, 'g');
/** Un signe qu'aucun lecteur ne sait lire : la saisie qui le porte n'est pas devinée. */
export const SEPARATION_NON_LUE = '¤';
/** La forme sans équivoque d'un nombre fractionnaire, et de quoi la relire. */
export const MOTIF_MIXTE = /\\mixte\{(\d+)\}\{(\d+)\}\{(\d+)\}/g;
const mixte = (entier, numerateur, denominateur) => `\\mixte{${entier}}{${numerateur}}{${denominateur}}`;
const plusPetiteQueUn = (numerateur, denominateur) => {
    const [n, d] = [Number(numerateur), Number(denominateur)];
    return Number.isSafeInteger(n) && Number.isSafeInteger(d) && n >= 1 && n < d;
};
/**
 * Récrit chaque nombre fractionnaire d'une écriture en `\mixte{E}{a}{b}`. Le
 * reste de l'écriture n'est pas touché. Idempotent.
 */
export function lireNombresFractionnaires(ecriture) {
    return (ecriture ?? '')
        .replace(EN_FRACTION, (tout, entier, _sep, n, d) => plusPetiteQueUn(n, d)
        ? mixte(entier, n, d)
        : `${entier}${SEPARATION_NON_LUE}\\frac{${n}}{${d}}`)
        .replace(EN_LIGNE, (tout, entier, _sep, n, d) => {
        /* « 12 500/2 » : une tranche de milliers, pas un numérateur. */
        if (n.length === 3 && entier.length <= 3)
            return tout;
        return plusPetiteQueUn(n, d)
            ? mixte(entier, n, d)
            : `${entier}${SEPARATION_NON_LUE}${n}/${d}`;
    });
}
/** Remet un nombre fractionnaire sous la forme d'une SOMME entre parenthèses : `(8+\frac{1}{12})`. */
export function mixtesEnSommes(ecriture) {
    return ecriture.replace(MOTIF_MIXTE, (_tout, entier, n, d) => `(${entier}+\\frac{${n}}{${d}})`);
}
//# sourceMappingURL=nombre-fractionnaire.js.map